import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import HoldingsPage from './HoldingsPage';
import TransactionsPage from './TransactionsPage';
import axios from 'axios';
import {
  Building2, TrendingUp, PieChart, BarChart3, Wallet,
  ArrowUpRight, ArrowDownRight, ChevronRight, RefreshCw, Plus, Eye, EyeOff,
  Briefcase, Landmark, CircleDollarSign, Shield, LineChart, CandlestickChart,
  Coins, Home, Import, AlertCircle, Zap, FileText, Globe, Package, Cpu, Layers,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced, maskCurrency } from '@/lib/market';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart as RechartsPie, Pie } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CHART_PALETTE = ['#ffd879','#d9e2c3','#a8c5da','#c9a86b','#9b8dc4','#8dc4a8','#c4a88d','#8da8c4','#b8c4a8','#c4b88d','#c4c48d','#a8a8c4'];

const EXT_SUB_TABS = [
  { value: 'invit',       label: 'InvITs',           icon: Zap },
  { value: 'sgb',         label: 'SGBs',             icon: Coins },
  { value: 'ncd',         label: 'NCDs / Bonds',     icon: FileText },
  { value: 'mld',         label: 'MLDs',             icon: TrendingUp },
  { value: 'derivatives', label: 'Derivatives',      icon: BarChart3 },
  { value: 'forex',       label: 'Forex',            icon: Globe },
  { value: 'commodity',   label: 'Commodities',      icon: Package },
  { value: 'crypto',      label: 'Digital Assets',   icon: Cpu },
  { value: 'insurance',   label: 'Insurance',        icon: Shield },
];

const ASSET_ICONS = {
  stocks: CandlestickChart, mf: PieChart, fo: LineChart, bonds: Landmark,
  etf: BarChart3, fd: CircleDollarSign, nps: Shield, pms: Briefcase,
  aif: Building2, gold: Coins, ppf: Wallet, realestate: Home,
};
const ASSET_GRADIENTS = {
  stocks: 'bg-white', mf: 'bg-white', fd: 'bg-white',
  gold: 'bg-white', etf: 'bg-white', fo: 'bg-white',
  bonds: 'bg-white', nps: 'bg-white', pms: 'bg-white',
  aif: 'bg-white', ppf: 'bg-white', realestate: 'bg-white',
};

// ─── Shared sub-components ────────────────────────────────────────────────

const pct = (v) => v != null ? `${+v >= 0 ? '+' : ''}${(+v).toFixed(2)}%` : '–';
const pctColor = (v) => (v == null || +v >= 0) ? 'text-emerald-600' : 'text-[#c97a3a]';

function StatBar({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {stats.map((s, i) => (
        <div key={i} className="bg-white border border-[#e5e5e5] rounded-lg shadow-sm p-4" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{s.label}</p>
          <p className="text-xl font-bold text-[#1a1a1a]">{s.value}</p>
          {s.sub && <p className="text-xs text-[#4a4a4a] mt-1">{s.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function ExtLoading() {
  return (
    <div className="text-center py-16 text-[#4a4a4a]">
      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-[#ffd87a]" />
      <p className="text-sm">Loading...</p>
    </div>
  );
}

function EmptyState({ icon: Icon = AlertCircle, msg = 'No data available' }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-12 h-12 text-[#4a4a4a] mx-auto mb-3" />
      <p className="text-sm text-[#4a4a4a]">{msg}</p>
    </div>
  );
}

function DataTable({ headers, children, empty, emptyMsg, emptyIcon }) {
  if (empty) return <EmptyState icon={emptyIcon} msg={emptyMsg} />;
  return (
    <div className="overflow-x-auto border border-[#e5e5e5]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#dae4c4]">
            {headers.map((h, i) => (
              <th key={i} className={`px-4 py-3 text-xs text-[#1a1a1a] uppercase tracking-wider whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children, right, color, mono }) {
  return (
    <td className={`px-4 py-3 ${right ? 'text-right' : ''} ${color || 'text-[#1a1a1a]'} ${mono ? 'font-mono' : ''}`}>
      {children}
    </td>
  );
}

// ─── Extended Tab Components ──────────────────────────────────────────────


function InvITTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalValue = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const avgYield = data.length ? data.reduce((s, d) => s + (d.yield_pct || 0), 0) / data.length : 0;
  return (
    <div>
      <StatBar stats={[
        { label: 'InvITs', value: data.length },
        { label: 'Current Value', value: fmt(totalValue) },
        { label: 'Invested', value: fmt(totalInvested) },
        { label: 'Avg Yield', value: `${avgYield.toFixed(2)}%` },
      ]} />
      <DataTable headers={['Name', 'Symbol', 'Units', 'Invested', 'Current Value', 'Gain %', 'Yield %']}
        empty={!data.length} emptyMsg="No InvIT positions" emptyIcon={Zap}>
        {data.map((d, i) => {
          const gain = (d.current_value || 0) - (d.invested || 0);
          const gPct = d.invested ? (gain / d.invested) * 100 : 0;
          return (
            <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
              <Td><p className="font-medium">{d.name}</p></Td>
              <Td right><span className="font-mono text-xs bg-[#f5f5f5] px-2 py-0.5">{d.symbol || '–'}</span></Td>
              <Td right>{d.units?.toLocaleString() || '–'}</Td>
              <Td right>{fmt(d.invested)}</Td>
              <Td right>{fmt(d.current_value)}</Td>
              <Td right color={pctColor(gPct)}>{pct(gPct)}</Td>
              <Td right color="text-emerald-600">{d.yield_pct != null ? `${d.yield_pct}%` : '–'}</Td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

function SGBTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalUnits = data.reduce((s, d) => s + (d.units || 0), 0);
  const totalValue = data.reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0);
  const avgRate = data.length ? data.reduce((s, d) => s + (d.interest_rate || 0), 0) / data.length : 0;
  return (
    <div>
      <StatBar stats={[
        { label: 'SGB Series', value: data.length },
        { label: 'Total Units', value: totalUnits.toLocaleString() },
        { label: 'Current Value', value: fmt(totalValue) },
        { label: 'Avg Interest Rate', value: `${avgRate.toFixed(2)}%` },
      ]} />
      <DataTable headers={['Series', 'Units', 'Issue Price', 'Current Price', 'Maturity', 'Interest Rate', 'Gain %']}
        empty={!data.length} emptyMsg="No SGB holdings" emptyIcon={Coins}>
        {data.map((d, i) => {
          const invested = (d.units || 0) * (d.issue_price || 0);
          const value = (d.units || 0) * (d.current_price || 0);
          const gPct = invested ? ((value - invested) / invested) * 100 : 0;
          return (
            <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
              <Td><p className="font-medium">{d.series || d.name}</p></Td>
              <Td right>{d.units?.toLocaleString() || '–'}</Td>
              <Td right>{d.issue_price != null ? fmt(d.issue_price) : '–'}</Td>
              <Td right>{d.current_price != null ? fmt(d.current_price) : '–'}</Td>
              <Td right>{d.maturity || '–'}</Td>
              <Td right color="text-emerald-600">{d.interest_rate != null ? `${d.interest_rate}%` : '–'}</Td>
              <Td right color={pctColor(gPct)}>{pct(gPct)}</Td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}


function NCDTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalValue = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const avgCoupon = data.length ? data.reduce((s, d) => s + (d.coupon_rate || 0), 0) / data.length : 0;
  return (
    <div>
      <StatBar stats={[
        { label: 'Bonds / NCDs', value: data.length },
        { label: 'Current Value', value: fmt(totalValue) },
        { label: 'Invested', value: fmt(totalInvested) },
        { label: 'Avg Coupon', value: `${avgCoupon.toFixed(2)}%` },
      ]} />
      <DataTable headers={['Name', 'Issuer', 'Coupon', 'Rating', 'Maturity', 'Invested', 'Current Value']}
        empty={!data.length} emptyMsg="No NCD / bond holdings" emptyIcon={FileText}>
        {data.map((d, i) => (
          <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
            <Td><p className="font-medium">{d.name}</p>{d.issuer && <p className="text-xs text-[#4a4a4a]">{d.issuer}</p>}</Td>
            <Td right>{d.issuer || '–'}</Td>
            <Td right color="text-emerald-600">{d.coupon_rate != null ? `${d.coupon_rate}%` : '–'}</Td>
            <Td right><span className="text-xs border border-[#e5e5e5] px-2 py-0.5">{d.rating || '–'}</span></Td>
            <Td right>{d.maturity || '–'}</Td>
            <Td right>{fmt(d.invested)}</Td>
            <Td right>{fmt(d.current_value)}</Td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

function MLDTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const avgReturn = data.length ? data.reduce((s, d) => s + (d.projected_return || 0), 0) / data.length : 0;
  return (
    <div>
      <StatBar stats={[
        { label: 'MLDs', value: data.length },
        { label: 'Total Invested', value: fmt(totalInvested) },
        { label: 'Avg Projected Return', value: `${avgReturn.toFixed(1)}%` },
        { label: 'Structure', value: 'Market-Linked', sub: 'principal protection varies' },
      ]} />
      <DataTable headers={['Name', 'Issuer', 'Linked To', 'Invested', 'Tenure', 'Maturity', 'Projected Return']}
        empty={!data.length} emptyMsg="No MLD positions" emptyIcon={TrendingUp}>
        {data.map((d, i) => (
          <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
            <Td><p className="font-medium">{d.name}</p></Td>
            <Td right>{d.issuer || '–'}</Td>
            <Td right>{d.linked_to || '–'}</Td>
            <Td right>{fmt(d.invested)}</Td>
            <Td right>{d.tenure_months != null ? `${d.tenure_months}M` : '–'}</Td>
            <Td right>{d.maturity || '–'}</Td>
            <Td right color="text-emerald-600">{d.projected_return != null ? `${d.projected_return}%` : '–'}</Td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

function DerivTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalMTM = data.reduce((s, d) => s + (d.mtm || 0), 0);
  const totalPnl = data.reduce((s, d) => s + (d.pnl || 0), 0);
  const types = [...new Set(data.map(d => d.type).filter(Boolean))];
  return (
    <div>
      <StatBar stats={[
        { label: 'Positions', value: data.length },
        { label: 'Total MTM', value: fmt(totalMTM) },
        { label: 'Total P&L', value: fmt(totalPnl), sub: totalPnl >= 0 ? 'net profit' : 'net loss' },
        { label: 'Instrument Types', value: types.length || '–' },
      ]} />
      <DataTable headers={['Name', 'Type', 'Underlying', 'Expiry', 'Lots', 'MTM Value', 'P&L']}
        empty={!data.length} emptyMsg="No derivative positions" emptyIcon={BarChart3}>
        {data.map((d, i) => (
          <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
            <Td><p className="font-medium">{d.name}</p></Td>
            <Td right><span className="text-xs border border-[#e5e5e5] px-2 py-0.5">{d.type || '–'}</span></Td>
            <Td right>{d.underlying || '–'}</Td>
            <Td right>{d.expiry || '–'}</Td>
            <Td right>{d.lots || '–'}</Td>
            <Td right>{fmt(d.mtm)}</Td>
            <Td right color={pctColor(d.pnl)}>{fmt(d.pnl)}</Td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

function ForexTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalPnl = data.reduce((s, d) => s + (d.pnl || 0), 0);
  return (
    <div>
      <StatBar stats={[
        { label: 'Pairs', value: data.length },
        { label: 'Total P&L', value: fmt(totalPnl) },
        { label: 'Long Positions', value: data.filter(d => d.position_type === 'Long').length },
        { label: 'Short Positions', value: data.filter(d => d.position_type === 'Short').length },
      ]} />
      <DataTable headers={['Pair', 'Units', 'Avg Rate', 'Current Rate', 'Position', 'P&L']}
        empty={!data.length} emptyMsg="No forex positions" emptyIcon={Globe}>
        {data.map((d, i) => (
          <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
            <Td mono><p className="font-medium">{d.pair}</p></Td>
            <Td right>{d.units?.toLocaleString() || '–'}</Td>
            <Td right>{d.avg_rate?.toFixed(4) || '–'}</Td>
            <Td right>{d.current_rate?.toFixed(4) || '–'}</Td>
            <Td right>
              <span className={`text-xs px-2 py-0.5 ${d.position_type === 'Long' ? 'text-emerald-700 bg-emerald-50' : 'text-[#c97a3a] bg-orange-50'}`}>
                {d.position_type || '–'}
              </span>
            </Td>
            <Td right color={pctColor(d.pnl)}>{fmt(d.pnl)}</Td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

function CommodityTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalPnl = data.reduce((s, d) => s + (d.pnl || 0), 0);
  const commodities = [...new Set(data.map(d => d.commodity).filter(Boolean))];
  return (
    <div>
      <StatBar stats={[
        { label: 'Positions', value: data.length },
        { label: 'Total P&L', value: fmt(totalPnl) },
        { label: 'Commodities', value: commodities.length },
        { label: 'Net Result', value: totalPnl >= 0 ? 'Profit' : 'Loss', sub: totalPnl >= 0 ? 'across positions' : 'across positions' },
      ]} />
      <DataTable headers={['Name', 'Commodity', 'Contract', 'Expiry', 'Lots', 'Avg Price', 'Current Price', 'P&L']}
        empty={!data.length} emptyMsg="No commodity positions" emptyIcon={Package}>
        {data.map((d, i) => (
          <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
            <Td><p className="font-medium">{d.name}</p></Td>
            <Td right>{d.commodity || '–'}</Td>
            <Td right>{d.contract || '–'}</Td>
            <Td right>{d.expiry || '–'}</Td>
            <Td right>{d.lots || '–'}</Td>
            <Td right>{d.avg_price != null ? fmt(d.avg_price) : '–'}</Td>
            <Td right>{d.current_price != null ? fmt(d.current_price) : '–'}</Td>
            <Td right color={pctColor(d.pnl)}>{fmt(d.pnl)}</Td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}

function CryptoTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalValue = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalGain = totalValue - totalInvested;
  const gainPct = totalInvested ? (totalGain / totalInvested) * 100 : 0;
  return (
    <div>
      <StatBar stats={[
        { label: 'Assets', value: data.length },
        { label: 'Current Value', value: fmt(totalValue) },
        { label: 'Invested', value: fmt(totalInvested) },
        { label: 'Total Gain', value: pct(gainPct) },
      ]} />
      <DataTable headers={['Asset', 'Symbol', 'Units', 'Avg Buy Price', 'Current Price', 'Invested', 'Current Value', 'Gain %']}
        empty={!data.length} emptyMsg="No digital asset positions" emptyIcon={Cpu}>
        {data.map((d, i) => {
          const gain = (d.current_value || 0) - (d.invested || 0);
          const gPct = d.invested ? (gain / d.invested) * 100 : 0;
          return (
            <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
              <Td><p className="font-medium">{d.name}</p></Td>
              <Td right><span className="font-mono text-xs bg-[#f5f5f5] px-2 py-0.5">{d.symbol || '–'}</span></Td>
              <Td right>{d.units?.toLocaleString() || '–'}</Td>
              <Td right>{d.avg_buy_price != null ? fmt(d.avg_buy_price) : '–'}</Td>
              <Td right>{d.current_price != null ? fmt(d.current_price) : '–'}</Td>
              <Td right>{fmt(d.invested)}</Td>
              <Td right>{fmt(d.current_value)}</Td>
              <Td right color={pctColor(gPct)}>{pct(gPct)}</Td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

function InsuranceTab({ data, loading, fmt }) {
  if (loading) return <ExtLoading />;
  const totalPremium = data.reduce((s, d) => s + (d.annual_premium || 0), 0);
  const totalSumAssured = data.reduce((s, d) => s + (d.sum_assured || 0), 0);
  const totalValue = data.reduce((s, d) => s + (d.current_value || 0), 0);
  return (
    <div>
      <StatBar stats={[
        { label: 'Policies', value: data.length },
        { label: 'Annual Premium', value: fmt(totalPremium) },
        { label: 'Sum Assured', value: fmt(totalSumAssured) },
        { label: 'Current Value', value: fmt(totalValue) },
      ]} />
      <DataTable headers={['Name', 'Insurer', 'Type', 'Annual Premium', 'Sum Assured', 'Invested', 'Current Value', 'Gain %']}
        empty={!data.length} emptyMsg="No insurance policies" emptyIcon={Shield}>
        {data.map((d, i) => {
          const gain = (d.current_value || 0) - (d.invested || 0);
          const gPct = d.invested ? (gain / d.invested) * 100 : (d.gain_pct || 0);
          return (
            <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f9f9f9]`}>
              <Td><p className="font-medium">{d.name}</p>{d.insurer && <p className="text-xs text-[#4a4a4a]">{d.insurer}</p>}</Td>
              <Td right>{d.insurer || '–'}</Td>
              <Td right><span className="text-xs border border-[#e5e5e5] px-2 py-0.5">{d.type || '–'}</span></Td>
              <Td right>{d.annual_premium != null ? fmt(d.annual_premium) : '–'}</Td>
              <Td right>{d.sum_assured != null ? fmt(d.sum_assured) : '–'}</Td>
              <Td right>{fmt(d.invested)}</Td>
              <Td right>{fmt(d.current_value)}</Td>
              <Td right color={pctColor(gPct)}>{pct(gPct)}</Td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

// ─── Others sub-page (lives inside Public Markets "Others" tab) ──────────────

function OthersSubPage({ fmt }) {
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('invit');
  const [extData, setExtData] = useState({});
  const [extLoading, setExtLoading] = useState(false);
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    if (!fetchedRef.current.has(activeSubTab)) {
      fetchedRef.current.add(activeSubTab);
      setExtLoading(true);
      axios.get(`${API}/other-assets/${activeSubTab}`)
        .then(r => setExtData(prev => ({ ...prev, [activeSubTab]: r.data || [] })))
        .catch(() => setExtData(prev => ({ ...prev, [activeSubTab]: [] })))
        .finally(() => setExtLoading(false));
    }
  }, [activeSubTab]);

  const d = extData[activeSubTab] || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex flex-wrap gap-1 p-1 bg-[#f5f5f5] border border-[#e5e5e5] rounded-lg">
        {EXT_SUB_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setActiveSubTab(t.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              activeSubTab === t.value
                ? 'bg-[#ffd87a] text-black font-medium'
                : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-white'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
        </div>
        <button
          onClick={() => navigate('/import')}
          className="flex items-center gap-2 px-4 py-2 bg-[#ffd87a] hover:bg-[#f5cc6a] text-black text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Holdings
        </button>
      </div>
      {extLoading && <ExtLoading />}
      {!extLoading && activeSubTab === 'invit'       && <InvITTab     data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'sgb'         && <SGBTab       data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'ncd'         && <NCDTab       data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'mld'         && <MLDTab       data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'derivatives' && <DerivTab     data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'forex'       && <ForexTab     data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'commodity'   && <CommodityTab data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'crypto'      && <CryptoTab    data={d} loading={false} fmt={fmt} />}
      {!extLoading && activeSubTab === 'insurance'   && <InsuranceTab data={d} loading={false} fmt={fmt} />}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────

export default function PublicMarketsDashboard() {
  const { user } = useAuth();
  const { assetClassMap } = useAppData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'markets';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showValues, setShowValues] = useState(true);
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [portfolios, setPortfolios] = useState([]);
  const [consolidation, setConsolidation] = useState(null);
  const [assetClassSummary, setAssetClassSummary] = useState([]);
  const [marketIndices, setMarketIndices] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [portfoliosRes, consolidationRes, assetSummaryRes, indicesRes, holdingsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/markets/portfolios`),
        axios.get(`${API}/analytics/consolidation`),
        axios.get(`${API}/analytics/exposure/asset_class`),
        axios.get(`${API}/markets/market/indices`).catch(() => ({ data: [] })),
        axios.get(`${API}/markets/holdings`),
        axios.get(`${API}/markets/transactions?limit=10`).catch(() => ({ data: [] })),
      ]);
      setPortfolios(portfoliosRes.data);
      setConsolidation(consolidationRes.data);
      setAssetClassSummary(assetSummaryRes.data?.items || []);
      setMarketIndices(indicesRes.data || []);
      setHoldings(holdingsRes.data);
      setRecentTransactions(transactionsRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    toast.success('Dashboard refreshed');
  };

  const formatCurrency = (value) => {
    if (!showValues) return maskCurrency(user?.market);
    return formatCurrencySpaced(value, user?.market);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(2) || 0}%`;
  };

  const AC_SUMMARY_NAMES = {
    stocks: 'Stocks', mf: 'Mutual Funds', fd: 'Fixed Deposits',
    gold: 'Gold/Silver', etf: 'ETFs', fo: 'F&O', bonds: 'Debt',
    nps: 'NPS', pms: 'PMS', aif: 'AIF', ppf: 'PPF/EPF', realestate: 'Real Estate',
  };

  const assetClasses = Object.entries(consolidation?.asset_allocation || {}).map(([key, percentage]) => {
    const summary = assetClassSummary.find(s => s.name === AC_SUMMARY_NAMES[key]);
    return {
      id: key,
      name: assetClassMap[key]?.label || key,
      icon: ASSET_ICONS[key] || Wallet,
      value: summary?.value || (consolidation?.total_value * percentage / 100) || 0,
      percentage,
      gain_percent: summary?.gain_percent || 0,
      isUp: (summary?.gain_percent || 0) >= 0,
      color: assetClassMap[key]?.data?.gradient || ASSET_GRADIENTS[key] || 'bg-[#1a1a1a]',
      holdings_count: summary?.holdings_count || 0,
    };
  }).sort((a, b) => b.value - a.value);

  const totalValue = consolidation?.total_value || 0;
  const totalInvested = consolidation?.total_invested || 0;
  const totalGain = consolidation?.total_gain || 0;
  const gainPercent = consolidation?.gain_percent || 0;
  const dayChange = consolidation?.day_change || 0;

  if (loading) {
    return (
      <div className="bg-[#f8fafc] flex items-center justify-center min-h-full">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#ffd87a] animate-spin mx-auto mb-4" />
          <p className="text-[#4a4a4a]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeAsset = searchParams.get('asset');
  const visualTab = activeTab === 'holdings' && activeAsset ? activeAsset : activeTab;
  const setTab = (tab, asset) => asset ? setSearchParams({ tab, asset }) : setSearchParams({ tab });
  const handleAssetClick = (assetId) => setSearchParams({ tab: 'holdings', asset: assetId });

  const TABS = [
    { value: 'markets',      label: 'Overview',      icon: TrendingUp },
    { value: 'stocks',       label: 'Equities',      icon: CandlestickChart, tab: 'holdings', asset: 'stocks' },
    { value: 'mf',           label: 'Mutual Funds',  icon: PieChart,         tab: 'holdings', asset: 'mf' },
    { value: 'etf',          label: 'ETFs',          icon: BarChart3,        tab: 'holdings', asset: 'etf' },
    { value: 'bonds',        label: 'Debt',          icon: Landmark,         tab: 'holdings', asset: 'bonds' },
    { value: 'pms',          label: 'PMS',           icon: Briefcase,        tab: 'holdings', asset: 'pms' },
    { value: 'aif',          label: 'AIF',           icon: Building2,        tab: 'holdings', asset: 'aif' },
    { value: 'nps',          label: 'NPS',           icon: Shield,           tab: 'holdings', asset: 'nps' },
    { value: 'fd',           label: 'Fixed Deposits',icon: CircleDollarSign, tab: 'holdings', asset: 'fd' },
    { value: 'gold',         label: 'Gold / Silver', icon: Coins,            tab: 'holdings', asset: 'gold' },
    { value: 'others',       label: 'Others',        icon: Layers },
    { value: 'holdings',     label: 'All Holdings',  icon: Wallet,           tab: 'holdings' },
    { value: 'transactions', label: 'Transactions',  icon: ArrowUpRight,     tab: 'transactions' },
  ];

  return (
    <div className="p-6" data-testid="public-markets-dashboard">
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginBottom: 22 }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: '#dae4c4', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <CandlestickChart size={22} color="#1a1a1a" />
        </div>
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 4 }}>Asset Dashboard</div>
          <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 34, letterSpacing: '-0.02em', lineHeight: 1.05, color: '#2a2a2a', margin: 0 }}>Public Markets</h1>
        </div>
      </div>

      <Tabs
        value={visualTab}
        onValueChange={v => { const t = TABS.find(x => x.value === v); if (t) setTab(t.tab || t.value, t.asset); }}
        className="w-full"
      >
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6 flex-wrap h-auto gap-1 p-1">
          {TABS.map(t => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5"
            >
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Existing tabs */}
        <TabsContent value="holdings"><HoldingsPage /></TabsContent>
        <TabsContent value="transactions"><TransactionsPage /></TabsContent>
        {TABS.filter(t => t.asset).map(t => (
          <TabsContent key={t.value} value={t.value}><HoldingsPage /></TabsContent>
        ))}

        <TabsContent value="others">
          <OthersSubPage fmt={formatCurrency} />
        </TabsContent>

        {/* Overview tab */}
        <TabsContent value="markets">
          <>
            {/* Top Controls */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#1a1a1a]">Portfolio Dashboard</h2>
                <p className="text-sm text-[#4a4a4a]">
                  {consolidation?.holdings_count || 0} holdings across {consolidation?.portfolios_count || 0} portfolios
                </p>
              </div>
              <div className="flex items-center gap-3">
                {portfolios.length > 0 && (
                  <Select value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
                    <SelectTrigger className="bg-white border-[#e5e5e5] text-[#1a1a1a] text-sm w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#e5e5e5]">
                      <SelectItem value="all" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">All Portfolios</SelectItem>
                      {portfolios.map((p) => (
                        <SelectItem key={p.id} value={p.id} className="text-[#1a1a1a] hover:bg-[#f5f5f5]">{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowValues(!showValues)} className="border-[#e5e5e5] text-[#4a4a4a]">
                  {showValues ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="border-[#e5e5e5] text-[#4a4a4a]">
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button onClick={() => setSearchParams({ tab: 'holdings', add: 'true' })} className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
                  <Plus className="w-4 h-4 mr-2" /> Add Holdings
                </Button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Stat band */}
              <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                {[
                  { k: 'Total Value', v: formatCurrency(totalValue), delta: dayChange !== 0 ? `${dayChange >= 0 ? '+' : ''}${formatCurrency(Math.abs(dayChange))} today` : 'Current FMV', tone: dayChange >= 0 ? '#059669' : '#e53535' },
                  { k: 'Invested', v: formatCurrency(totalInvested), delta: 'Cost basis', tone: '#666' },
                  { k: 'Total Gain', v: formatCurrency(Math.abs(totalGain)), delta: formatPercent(gainPercent), tone: totalGain >= 0 ? '#059669' : '#e53535' },
                  { k: 'XIRR', v: formatPercent(consolidation?.xirr || 0), delta: 'Annualised return', tone: (consolidation?.xirr || 0) >= 0 ? '#059669' : '#e53535' },
                ].map((cell, i) => (
                  <div key={i} style={{ flex: 1, minWidth: 130, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: '5px 0 4px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
                    <div style={{ fontSize: 10.5, fontWeight: 500, color: cell.tone }}>{cell.delta}</div>
                  </div>
                ))}
              </div>

              {/* Charts row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
                <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 14 }}>Allocation by Asset Class</div>
                  {assetClasses.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={assetClasses.slice(0, 8).map(a => ({ name: a.name?.replace('Mutual Funds','MF').replace('Equities','Stocks'), value: a.value || 0 }))} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 9.5, fill: '#888' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v} />
                        <Tooltip formatter={(v) => [formatCurrency(v), 'Value']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5e5' }} />
                        <Bar dataKey="value" radius={[4,4,0,0]}>
                          {assetClasses.slice(0,8).map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ height: 200, display: 'grid', placeItems: 'center', color: '#aaa', fontSize: 13 }}>No data</div>}
                </div>

                <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 10 }}>Portfolio Split</div>
                  {assetClasses.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 14, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <RechartsPie width={150} height={200}>
                          <Pie data={assetClasses.slice(0,8).map((a,i)=>({name:a.name,value:a.value||0}))} cx={75} cy={100} innerRadius={52} outerRadius={72} dataKey="value" paddingAngle={2}>
                            {assetClasses.slice(0,8).map((_,i) => <Cell key={i} fill={CHART_PALETTE[i%CHART_PALETTE.length]} />)}
                          </Pie>
                        </RechartsPie>
                      </div>
                      <ul style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                        {assetClasses.slice(0,7).map((a,i) => {
                          const p = a.percentage || 0;
                          return (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: i < 6 ? '1px solid #f0f0f0' : 'none', fontSize: 10.5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: CHART_PALETTE[i%CHART_PALETTE.length], flexShrink: 0 }} />
                              <span style={{ flex: 1, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                              <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{p < 0.1 ? '<0.1' : p.toFixed(1)}%</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: '#aaa', fontSize: 12 }}>No data</div>}
                </div>
              </div>

              {/* Market Indices */}
              {marketIndices.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  {marketIndices.slice(0, 4).map((index, i) => (
                    <div key={i} style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      <div style={{ fontSize: 10, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{index.name}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 2 }}>{index.value?.toLocaleString()}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: index.change >= 0 ? '#059669' : '#e53535', fontWeight: 600 }}>
                        {index.change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {formatPercent(index.change_percent)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Holdings + Recent Transactions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8' }}>Top Holdings</span>
                    <button onClick={() => setTab('holdings')} style={{ fontSize: 11, color: '#ffd879', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>View All</button>
                  </div>
                  {holdings.filter(h => h.asset_class !== 'fd' && h.asset_class !== 'cash').length > 0
                    ? holdings.filter(h => h.asset_class !== 'fd' && h.asset_class !== 'cash').slice(0, 6).map((h, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 5 ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 26, height: 26, borderRadius: 8, background: '#f5f5f5', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700, color: '#888' }}>{i+1}</span>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{h.symbol}</div>
                            <div style={{ fontSize: 10.5, color: '#888' }}>{h.name?.substring(0, 22)}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{formatCurrency(h.current_value)}</div>
                          <div style={{ fontSize: 10.5, fontWeight: 600, color: (h.gain_percent||0) >= 0 ? '#059669' : '#e53535' }}>{formatPercent(h.gain_percent)}</div>
                        </div>
                      </div>
                    ))
                    : <div style={{ color: '#aaa', textAlign: 'center', padding: '24px 0', fontSize: 12 }}>No holdings yet</div>}
                </div>

                <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8' }}>Recent Transactions</span>
                    <button onClick={() => setTab('transactions')} style={{ fontSize: 11, color: '#ffd879', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>View All</button>
                  </div>
                  {recentTransactions.length > 0
                    ? recentTransactions.slice(0, 6).map((txn, i) => {
                        const isBuy = txn.transaction_type === 'BUY' || txn.transaction_type === 'SIP';
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 5 ? '1px solid #f0f0f0' : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ width: 26, height: 26, borderRadius: 8, background: isBuy ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', display: 'grid', placeItems: 'center' }}>
                                {isBuy ? <ArrowDownRight size={13} style={{ color: '#059669' }} /> : <ArrowUpRight size={13} style={{ color: '#e53535' }} />}
                              </span>
                              <div>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{txn.symbol}</div>
                                <div style={{ fontSize: 10.5, color: '#888' }}>{txn.transaction_type} · {txn.date?.split('T')[0]}</div>
                              </div>
                            </div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{formatCurrency(txn.amount)}</div>
                          </div>
                        );
                      })
                    : <div style={{ color: '#aaa', textAlign: 'center', padding: '24px 0', fontSize: 12 }}>No transactions yet</div>}
                </div>
              </div>
            </div>
          </>
        </TabsContent>
      </Tabs>
    </div>
  );
}
